import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Snackbar, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * 予約設定ハブ各タブの共通部品。
 * - API エラーメッセージの整形
 * - 保存成功 Snackbar / エラー表示の状態管理フック
 * - 時間帯（open-close）リストの編集 UI（営業時間・特定日で共用）
 * - 未保存変更の sticky 保存バー（フィードバック3）
 */

/** 時間帯 1 件（"HH:mm" 文字列） */
export interface TimeRange {
  open: string;
  close: string;
}

// createApiRequest はHTTPエラー時にレスポンス本文(JSON文字列)を Error.message に入れる。
// thunk は rejectWithValue(message) するため、unwrap() で捕捉した値は string のことが多い。
// BE は {status,message,timestamp} を返すため、message を取り出して表示する。
export const extractApiMessage = (e: unknown): string => {
  // rejectWithValue で構造化オブジェクト（{message,...}）を返す thunk があるため、
  // オブジェクトの message フィールドを優先して取り出す（B4/B5 の ReservationConflict 等）。
  if (e && typeof e === 'object' && !(e instanceof Error)) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  const raw = typeof e === 'string' ? e : e instanceof Error ? e.message : String(e ?? '');
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

/** 保存成功 Snackbar とエラー Alert 用の状態をまとめたフック */
export const useSaveFeedback = () => {
  const [savedOpen, setSavedOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showSaved = useCallback(() => {
    setError(null);
    setSavedOpen(true);
  }, []);
  const closeSaved = useCallback(() => setSavedOpen(false), []);
  const showError = useCallback((e: unknown) => setError(extractApiMessage(e)), []);

  return { savedOpen, closeSaved, showSaved, error, setError, showError };
};

/** 保存成功時の共通 Snackbar */
export const SavedSnackbar: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => (
  <Snackbar
    open={open}
    autoHideDuration={3000}
    onClose={onClose}
    message="保存しました"
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
  />
);

/** タブ切替・離脱の確認文言（ReservationSettings と各タブで共通） */
export const UNSAVED_CONFIRM_MESSAGE =
  '未保存の変更があります。保存せずに移動すると変更内容は失われます。よろしいですか？';

/**
 * dirty 状態を親（ReservationSettings）へ通知するフック。
 * タブ切替時の確認ダイアログ判定に使う。アンマウント時は false へ戻す。
 */
export const useDirtyNotifier = (
  dirty: boolean,
  onDirtyChange?: (dirty: boolean) => void,
): void => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );
};

interface UnsavedChangesBarProps {
  /** true のときのみ表示する（未編集・保存済みは非表示） */
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  /** サーバ値へフォームを戻す */
  onDiscard: () => void;
  /**
   * dirty でなくても保存バーを表示する（例: 受付設定が一度も保存されていない店舗）。
   * このとき未変更（!dirty）なら「破棄」ボタンは隠し、文言も未保存向けにする。
   */
  forceShow?: boolean;
}

/**
 * 未保存の変更がある間だけ画面下部に張り付く保存バー（フィードバック3・保存し忘れ防止）。
 * 各タブのルート要素の末尾に置く（position: sticky bottom:0）。
 * forceShow=true の場合は変更がなくても表示し、初回保存の導線として使う。
 */
export const UnsavedChangesBar: React.FC<UnsavedChangesBarProps> = ({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  forceShow = false,
}) => {
  if (!dirty && !forceShow) return null;
  // 未変更で forceShow のみ（＝初回保存を促す状態）は「破棄」を出さず保存のみにする
  const neverSavedOnly = !dirty && forceShow;
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
        p: 1.5,
        mt: 2,
        borderTop: '2px solid',
        borderColor: 'warning.main',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {neverSavedOnly ? 'まだ保存されていません' : '未保存の変更があります'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {!neverSavedOnly && (
          <Button size="small" color="inherit" onClick={onDiscard} disabled={saving}>
            破棄
          </Button>
        )}
        <Button size="small" variant="contained" onClick={onSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : '保存'}
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * タブ冒頭の説明文（この設定は何にどう効くか）。
 * 文言は FE/iOS で共通のためタスク定義の例文から変えないこと。
 */
export const TabDescription: React.FC<{ text: string }> = ({ text }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
    {text}
  </Typography>
);

/**
 * "HH:mm" 同士の妥当性チェック（両方入力済みなら OK）。
 * close <= open は「翌日 close」（日跨ぎ営業。例: 17:00〜翌03:00）として BE 側でも有効値。
 */
export const isValidTimeRange = (r: TimeRange): boolean => r.open !== '' && r.close !== '';

/** 日跨ぎ（close <= open → 翌日 close 扱い）かどうか */
export const isOvernightRange = (r: TimeRange): boolean =>
  r.open !== '' && r.close !== '' && r.close <= r.open;

/** 'HH:mm〜HH:mm'（日跨ぎは 'HH:mm〜翌HH:mm'）の表示用文字列 */
export const formatTimeRange = (r: TimeRange): string =>
  `${r.open}〜${isOvernightRange(r) ? '翌' : ''}${r.close}`;

interface TimeRangeListEditorProps {
  ranges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
  disabled?: boolean;
  /** 0 件時に表示する文言（例: 定休日） */
  emptyLabel?: string;
}

/** 時間帯（open-close）を複数編集するリスト。ネイティブ time 入力を使う。 */
export const TimeRangeListEditor: React.FC<TimeRangeListEditorProps> = ({
  ranges,
  onChange,
  disabled = false,
  emptyLabel,
}) => {
  const update = (index: number, key: keyof TimeRange, value: string) => {
    onChange(ranges.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };
  const remove = (index: number) => {
    onChange(ranges.filter((_, i) => i !== index));
  };
  const add = () => {
    // 追加時のデフォルトは 00:00〜23:00（フィードバック1）
    onChange([...ranges, { open: '00:00', close: '23:00' }]);
  };

  return (
    <Box>
      {ranges.length === 0 && emptyLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          {emptyLabel}
        </Typography>
      )}
      {ranges.map((range, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <TextField
            type="time"
            size="small"
            value={range.open}
            onChange={(e) => update(index, 'open', e.target.value)}
            disabled={disabled}
            inputProps={{ 'aria-label': '開始時刻' }}
          />
          <Typography variant="body2">〜</Typography>
          {/* 日跨ぎ（close <= open）は「翌日 close」扱いであることを明示する */}
          {isOvernightRange(range) && (
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              翌
            </Typography>
          )}
          <TextField
            type="time"
            size="small"
            value={range.close}
            onChange={(e) => update(index, 'close', e.target.value)}
            disabled={disabled}
            inputProps={{ 'aria-label': '終了時刻' }}
          />
          <IconButton
            size="small"
            onClick={() => remove(index)}
            disabled={disabled}
            aria-label="時間帯を削除"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={disabled}>
        時間帯を追加
      </Button>
    </Box>
  );
};

/** number 入力の onChange 用。空文字や不正値は 0 に丸め、負値を防ぐ。 */
export const toNonNegativeInt = (value: string): number => {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.floor(n);
};
